import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUserQuizHistory } from '../components/apiClient';
import { useJWTAuth } from '../components/JWTAuthContext';

interface QuizHistoryItem {
  quiz_id: number;
  quiz_name: string;
  quiz_description: string;
  quiz_reference: string;
  quiz_type: string;
  quiz_question_type?: string;
  attempt_date: string;
  completed_questions: number;
  total_questions: number;
  score: number;
  time_spent: number;
  last_attempt: string;
}

const PYQHistory: React.FC = () => {
  const { user } = useJWTAuth();
  const navigate = useNavigate();
  const [filteredHistory, setFilteredHistory] = useState<QuizHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchQuizHistory = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const response = await getUserQuizHistory(user.id, 'pyq');
      if (response && response.result) {
        setFilteredHistory(response.result);
      }
    } catch (error) {
      console.error('Failed to fetch PYQ quiz history:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchQuizHistory();
  }, [fetchQuizHistory]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredHistory.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewResults = (quizReference: string) => {
    navigate(`/quiz-results-detailed/${quizReference}`);
  };

  // const handleRetakeQuiz = (quizReference: string) => {
  //   navigate(`/quiz/${quizReference}`);
  // };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getScoreBadgeClass = (score: number) => {
    if (score >= 80) return 'bg-success';
    if (score >= 60) return 'bg-warning';
    return 'bg-danger';
  };

  return (
    <div className="container-fluid py-5" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      <div className="container">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div>
                <h2 className="fw-bold mb-2" style={{ 
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  <i className="fas fa-archive me-3"></i>
                  PYQ Quiz History
                </h2>
                <p className="text-muted mb-0">View all your Previous Year Question quiz attempts</p>
              </div>
              <div className="d-flex gap-2">
                <Link 
                  to="/pyq-quiz-generator" 
                  className="btn btn-primary"
                  style={{ borderRadius: '12px' }}>
                  <i className="fas fa-plus me-2"></i>
                  Create New PYQ Quiz
                </Link>
                <Link 
                  to="/dashboard" 
                  className="btn btn-outline-secondary"
                  style={{ borderRadius: '12px' }}>
                  <i className="fas fa-arrow-left me-2"></i>
                  Back to Dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row mb-4 g-3">
          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
              <div className="card-body text-center p-4">
                <i className="fas fa-clipboard-list text-primary mb-2" style={{ fontSize: '2rem' }}></i>
                <h3 className="fw-bold mb-1">{filteredHistory.length}</h3>
                <p className="text-muted mb-0 small">Total PYQ Quizzes</p>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
              <div className="card-body text-center p-4">
                <i className="fas fa-check-circle text-success mb-2" style={{ fontSize: '2rem' }}></i>
                <h3 className="fw-bold mb-1">
                  {filteredHistory.reduce((acc, quiz) => acc + quiz.completed_questions, 0)}
                </h3>
                <p className="text-muted mb-0 small">Questions Attempted</p>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
              <div className="card-body text-center p-4">
                <i className="fas fa-percentage text-warning mb-2" style={{ fontSize: '2rem' }}></i>
                <h3 className="fw-bold mb-1">
                  {filteredHistory.length > 0 
                    ? Math.round(filteredHistory.reduce((acc, quiz) => acc + quiz.score, 0) / filteredHistory.length)
                    : 0}%
                </h3>
                <p className="text-muted mb-0 small">Average Score</p>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-6">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
              <div className="card-body text-center p-4">
                <i className="fas fa-clock text-info mb-2" style={{ fontSize: '2rem' }}></i>
                <h3 className="fw-bold mb-1">
                  {formatTime(filteredHistory.reduce((acc, quiz) => acc + quiz.time_spent, 0))}
                </h3>
                <p className="text-muted mb-0 small">Total Time Spent</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quiz History Table */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
          <div className="card-body p-4">
            {isLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted mt-3 mb-0">Loading PYQ quiz history...</p>
              </div>
            ) : currentItems.length > 0 ? (
              <>
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th className="border-0 fw-bold" style={{ borderRadius: '12px 0 0 12px' }}>Quiz Name</th>
                        <th className="border-0 fw-bold text-center">Date</th>
                        <th className="border-0 fw-bold text-center">Questions</th>
                        <th className="border-0 fw-bold text-center">Score</th>
                        <th className="border-0 fw-bold text-center">Time</th>
                        <th className="border-0 fw-bold text-center" style={{ borderRadius: '0 12px 12px 0' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((quiz, index) => (
                        <tr key={index}>
                          <td>
                            <div>
                              <h6 className="mb-1 fw-bold">{quiz.quiz_name || 'Untitled Quiz'}</h6>
                              {quiz.quiz_description && (
                                <small className="text-muted">{quiz.quiz_description}</small>
                              )}
                            </div>
                          </td>
                          <td className="text-center">
                            <small className="text-muted">{formatDate(quiz.attempt_date)}</small>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-light text-dark">
                              {quiz.completed_questions}/{quiz.total_questions}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className={`badge ${getScoreBadgeClass(quiz.score)}`}>
                              {quiz.score.toFixed(1)}%
                            </span>
                          </td>
                          <td className="text-center">
                            <small className="text-muted">
                              <i className="fas fa-clock me-1"></i>
                              {formatTime(quiz.time_spent)}
                            </small>
                          </td>
                          <td className="text-center">
                            <div className="btn-group" role="group">
                              <button
                                onClick={() => handleViewResults(quiz.quiz_reference)}
                                className="btn btn-sm btn-outline-primary"
                                style={{ borderRadius: '8px' }}
                                title="View Results">
                                <i className="fas fa-eye"></i>
                              </button>
                              {/* <button
                                onClick={() => handleRetakeQuiz(quiz.quiz_reference)}
                                className="btn btn-sm btn-outline-success"
                                style={{ borderRadius: '0 8px 8px 0' }}
                                title="Retake Quiz">
                                <i className="fas fa-redo"></i>
                              </button> */}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-3">
                    <div className="text-muted">
                      Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredHistory.length)} of {filteredHistory.length} quizzes
                    </div>
                    <nav>
                      <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => paginate(1)}
                            disabled={currentPage === 1}
                            style={{ borderRadius: '8px 0 0 0' }}
                            title="First Page">
                            <i className="fas fa-angle-double-left"></i>
                          </button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            title="Previous Page">
                            <i className="fas fa-chevron-left"></i>
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          // Show first page, last page, current page, and pages around current
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => paginate(pageNumber)}>
                                  {pageNumber}
                                </button>
                              </li>
                            );
                          } else if (
                            pageNumber === currentPage - 2 ||
                            pageNumber === currentPage + 2
                          ) {
                            return <li key={pageNumber} className="page-item disabled"><span className="page-link">...</span></li>;
                          }
                          return null;
                        })}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            title="Next Page">
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => paginate(totalPages)}
                            disabled={currentPage === totalPages}
                            style={{ borderRadius: '0 8px 8px 0' }}
                            title="Last Page">
                            <i className="fas fa-angle-double-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-5">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ 
                    width: '100px', 
                    height: '100px',
                    backgroundColor: '#f8f9fa',
                    border: '3px dashed #dee2e6'
                  }}>
                  <i className="fas fa-archive text-muted" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="text-muted mb-3">No PYQ Quizzes Yet</h5>
                <p className="text-muted mb-4">
                  Start practicing with Previous Year Questions to improve your exam preparation!
                </p>
                <Link 
                  to="/pyq-quiz-generator" 
                  className="btn btn-primary btn-lg"
                  style={{ borderRadius: '12px' }}>
                  <i className="fas fa-plus me-2"></i>
                  Create Your First PYQ Quiz
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PYQHistory;
